import { useState, useEffect } from "react";
import { Box, Button, TextField, Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useCRUD } from "../../../api/crud";
import { EQUIPMENT_PRODUCERS_ENDPOINT, ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../../consts";
import styles from "../dynamicForm/dynamicForm.module.css";

interface ProducerFormProps {
  id?: string;
  onSuccess?: (data?: any) => void;
}

interface ModelInput {
  id?: number;
  name: string;
}

export default function ProducerForm({ id, onSuccess }: ProducerFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { get, create, update, loading, error } = useCRUD(EQUIPMENT_PRODUCERS_ENDPOINT);
  const [name, setName] = useState("");
  const [models, setModels] = useState<ModelInput[]>([]);
  const [modelInputValue, setModelInputValue] = useState("");

  const isEdit = Boolean(id);

  useEffect(() => {
    if (isEdit && id) {
      get(id).then((res) => {
        if (res?.data) {
          setName(res.data.name || "");
          const modelList = Array.isArray(res.data.models)
            ? res.data.models.map((m: { id?: number; name: string }) => ({ id: m.id, name: m.name || "" }))
            : [];
          setModels(modelList);
        }
      });
    }
  }, [isEdit, id, get]);

  const addModelFromInput = () => {
    const trimmed = modelInputValue.trim();
    if (!trimmed) return;
    setModels((prev) => [...prev, { name: trimmed }]);
    setModelInputValue("");
  };

  const removeModel = (index: number) => {
    setModels((prev) => prev.filter((_, i) => i !== index));
  };

  const handleModelInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addModelFromInput();
    }
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      enqueueSnackbar(t("Name is required"), ERROR_SNACKBAR);
      return;
    }

    const modelsPayload = models
      .map((m) => m.name.trim())
      .filter(Boolean)
      .map((modelName) => ({ name: modelName }));

    if (isEdit && id) {
      const payload: { name: string; models: { id?: number; name: string }[] } = {
        name: trimmedName,
        models: models.map((m) => (m.id != null ? { id: m.id, name: m.name.trim() || m.name } : { name: m.name.trim() })).filter((m) => (m as { name: string }).name),
      };
      if (payload.models.length === 0) payload.models = [];
      const result = await update({ id, name: trimmedName, models: payload.models });
      if (result?.success) {
        enqueueSnackbar(t("Producer updated successfully"), SUCCESS_SNACKBAR);
        onSuccess?.(result.data);
      } else {
        const msg =
          (typeof result?.errorData === "string" ? result.errorData : (result?.errorData as { message?: string })?.message) ||
          t("There was an error, please review the form.");
        enqueueSnackbar(msg, ERROR_SNACKBAR);
      }
    } else {
      const result = await create({
        name: trimmedName,
        models: modelsPayload.length > 0 ? modelsPayload : undefined,
      });
      if (result?.data?.id != null) {
        enqueueSnackbar(t("Producer created successfully"), SUCCESS_SNACKBAR);
        onSuccess?.(result.data);
      } else {
        const msg =
          (typeof result?.errorData === "string" ? result.errorData : (result?.errorData as { message?: string })?.message) ||
          t("There was an error, please review the form.");
        enqueueSnackbar(msg, ERROR_SNACKBAR);
      }
    }
  };

  const errorMessage = error && (typeof error === "string" ? error : (error as { message?: string })?.message);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField
        size="small"
        label={t("Name")}
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        fullWidth
      />
      <Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
          <TextField
            size="small"
            label={t("Model name")}
            value={modelInputValue}
            onChange={(e) => setModelInputValue(e.target.value)}
            onKeyDown={handleModelInputKeyDown}
            placeholder={t("Type and press Enter or Add")}
            fullWidth
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addModelFromInput}
            sx={{ flexShrink: 0, height: 40, minHeight: 40, display: "inline-flex", alignItems: "center" }}
          >
            {t("Add")}
          </Button>
        </Box>
        {models.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {models.map((model, index) => (
              <Chip
                key={model.id != null ? model.id : `new-${index}`}
                label={model.name}
                onDelete={() => removeModel(index)}
                size="small"
              />
            ))}
          </Box>
        )}
      </Box>
      {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" color="primary" disabled={loading} onClick={handleSubmit}>
          {loading ? t("Processing...") : t("Submit")}
        </Button>
      </Box>
    </Box>
  );
}
